import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { FiEdit, FiSave } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { ChainBadge } from '@/components/common/ChainBadge'
import { DataTable } from '@/components/common/DataTable'
import type { ActionButton, ColumnConfig } from '@/components/common/DataTable'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { CacheRefreshIconButton, RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useToast } from '@/hooks/useToast'
import {
  useRefreshSystemRpcEndpointsCache,
  useSystemRpcEndpoints,
  useUpdateSystemRpcEndpoint,
} from '@/hooks/useSystemConfigData'
import type { SystemRpcEndpointTemplate } from '@/services/admin/systemService'

type RpcEndpointsTabProps = {
  cardBg: string
}

type RpcEndpointFormState = {
  urlTemplate: string
  provider: string
  priority: string
  isActive: boolean
}

const PROVIDER_OPTIONS = [
  'alchemy',
  'helius',
  'ankr',
  'trongrid',
  'tronscan',
  'public',
  'custom',
  'explorer',
]

const toDateLabel = (value?: string): string => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString()
}

export function RpcEndpointsTab(props: RpcEndpointsTabProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const { data, isFetching, refetch } = useSystemRpcEndpoints()
  const refreshMutation = useRefreshSystemRpcEndpointsCache()
  const updateMutation = useUpdateSystemRpcEndpoint()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [editingRow, setEditingRow] = useState<SystemRpcEndpointTemplate | null>(null)
  const [filters, setFilters] = useState({
    search: '',
    provider: 'all',
    status: 'all' as 'all' | 'active' | 'inactive',
    limit: 100,
  })
  const [form, setForm] = useState<RpcEndpointFormState>({
    urlTemplate: '',
    provider: 'public',
    priority: '1',
    isActive: true,
  })

  const rows = data ?? []
  const providerOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      ...Array.from(new Set(rows.map(row => row.provider))).sort().map(provider => ({
        value: provider,
        label: provider,
      })),
    ],
    [rows, t]
  )
  const statusOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      { value: 'active', label: t('common.active') },
      { value: 'inactive', label: t('common.inactive') },
    ],
    [t]
  )
  const filteredRows = useMemo(() => {
    const needle = filters.search.trim().toLowerCase()
    return rows
      .filter(row => filters.provider === 'all' || row.provider === filters.provider)
      .filter(row => {
        if (filters.status === 'all') return true
        return filters.status === 'active' ? row.isActive : !row.isActive
      })
      .filter(row => {
        if (!needle) return true
        return (
          row.chainCode.toLowerCase().includes(needle)
          || row.endpointKey.toLowerCase().includes(needle)
          || row.provider.toLowerCase().includes(needle)
          || row.urlTemplate.toLowerCase().includes(needle)
        )
      })
      .slice(0, filters.limit)
  }, [filters, rows])

  const handleOpenEdit = (row: SystemRpcEndpointTemplate) => {
    setEditingRow(row)
    setForm({
      urlTemplate: row.urlTemplate,
      provider: row.provider,
      priority: String(row.priority),
      isActive: row.isActive,
    })
    onOpen()
  }

  const columns = useMemo<ColumnConfig<SystemRpcEndpointTemplate>[]>(() => ([
    {
      key: 'chainCode',
      title: t('systemConfig.rpcEndpoints.columns.chain', { defaultValue: 'Chain' }),
      width: '14%',
      render: (_, row) => (
        <ChainBadge
          chainId={row.chainCode}
          chainCode={row.chainCode}
          isTestnet={row.isTestnet}
        />
      ),
    },
    {
      key: 'endpointKey',
      title: t('systemConfig.rpcEndpoints.columns.endpoint', { defaultValue: 'Endpoint' }),
      width: '10%',
      render: (_, row) => row.endpointKey,
    },
    {
      key: 'provider',
      title: t('systemConfig.rpcEndpoints.columns.provider', { defaultValue: 'Provider' }),
      width: '10%',
      render: (_, row) => row.provider,
    },
    {
      key: 'priority',
      title: t('systemConfig.rpcEndpoints.columns.priority', { defaultValue: 'Priority' }),
      width: '8%',
      align: 'center',
      render: (_, row) => row.priority,
    },
    {
      key: 'isActive',
      title: t('common.status'),
      width: '8%',
      align: 'center',
      render: (_, row) => (
        <Badge colorScheme={row.isActive ? 'green' : 'gray'}>
          {row.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
    },
    {
      key: 'urlTemplate',
      title: t('systemConfig.rpcEndpoints.columns.urlTemplate', { defaultValue: 'URL template' }),
      render: (_, row) => (
        <Text noOfLines={2} title={row.urlTemplate}>
          {row.urlTemplate}
        </Text>
      ),
    },
    {
      key: 'updatedAt',
      title: t('common.updatedAt', { defaultValue: 'Updated at' }),
      width: '14%',
      render: (_, row) => toDateLabel(row.updatedAt),
      noWrap: true,
    },
  ]), [t])

  const actions = useMemo(() => {
    return (row: SystemRpcEndpointTemplate): ActionButton[] => [
      {
        label: t('common.edit'),
        icon: <FiEdit />,
        onClick: () => handleOpenEdit(row),
      },
    ]
  }, [t])

  const handleSave = async () => {
    if (!editingRow) {
      return
    }

    const priority = Number(form.priority)
    if (!Number.isInteger(priority) || priority < 0) {
      toast.error({
        title: t('systemConfig.rpcEndpoints.invalidPriority', { defaultValue: 'Priority must be an integer >= 0' }),
      })
      return
    }

    try {
      await updateMutation.mutateAsync({
        id: editingRow.id,
        payload: {
          urlTemplate: form.urlTemplate.trim(),
          provider: form.provider.trim().toLowerCase(),
          priority,
          isActive: form.isActive,
        },
      })
      toast.success({
        title: t('common.saveSuccess', { defaultValue: 'Saved' }),
      })
      onClose()
      await refetch()
    } catch (error) {
      toast.error({
        title: t('common.saveFailed', { defaultValue: 'Save failed' }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const handleRefreshCache = async () => {
    try {
      await refreshMutation.mutateAsync()
      toast.success({
        title: t('systemConfig.actions.refreshCacheSuccess', { defaultValue: 'Cache refreshed' }),
      })
    } catch (error) {
      toast.error({
        title: t('systemConfig.actions.refreshCacheFailed', { defaultValue: 'Cache refresh failed' }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return (
    <>
      <Card bg={props.cardBg}>
        <CardHeader>
          <Flex
            direction={{ base: 'column', xl: 'row' }}
            align={{ base: 'stretch', xl: 'end' }}
            justify="space-between"
            gap={3}
          >
            <HStack spacing={3} align="end" flexWrap={{ base: 'wrap', xl: 'nowrap' }} flex="1" minW={0}>
              <Heading size="sm" minW="fit-content">
                {t('systemConfig.tabs.rpcEndpoints', { defaultValue: 'rpc endpoints' })}
              </Heading>
              <FormControl minW={{ base: '100%', md: '220px' }} maxW={{ xl: '220px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('common.search', { defaultValue: 'Search' })}
                </FormLabel>
                <Input
                  size="xs"
                  value={filters.search}
                  onChange={event => setFilters(prev => ({ ...prev, search: event.target.value }))}
                  placeholder={t('common.search', { defaultValue: 'Search' })}
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '140px' }} maxW={{ xl: '140px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('systemConfig.rpcEndpoints.columns.provider', { defaultValue: 'Provider' })}
                </FormLabel>
                <DropdownSelect
                  value={filters.provider}
                  options={providerOptions}
                  onChange={value => setFilters(prev => ({ ...prev, provider: value }))}
                  size="xs"
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '120px' }} maxW={{ xl: '120px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('common.status')}
                </FormLabel>
                <DropdownSelect
                  value={filters.status}
                  options={statusOptions}
                  onChange={value =>
                    setFilters(prev => ({ ...prev, status: value as typeof filters.status }))
                  }
                  size="xs"
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '100px' }} maxW={{ xl: '100px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('common.limit')}
                </FormLabel>
                <DropdownSelect
                  value={String(filters.limit)}
                  options={[50, 100, 200, 500].map(limit => ({
                    value: String(limit),
                    label: String(limit),
                  }))}
                  onChange={value => setFilters(prev => ({ ...prev, limit: Number(value) }))}
                  size="xs"
                />
              </FormControl>
            </HStack>
            <HStack spacing={2} justify={{ base: 'flex-start', xl: 'flex-end' }} flexShrink={0}>
              <RefreshIconButton
                label={t('common.refresh')}
                onClick={() => refetch()}
                isLoading={isFetching}
              />
              <CacheRefreshIconButton
                label={t('systemConfig.actions.refreshCache', { defaultValue: 'Refresh Cache' })}
                onClick={handleRefreshCache}
                isLoading={refreshMutation.isPending}
              />
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          <DataTable<SystemRpcEndpointTemplate>
            data={filteredRows}
            loading={isFetching}
            columns={columns}
            actions={actions}
            showFilterBar={false}
            showRefresh={false}
            emptyMessage={t('common.noData', { defaultValue: 'No data' })}
          />
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader>{t('systemConfig.rpcEndpoints.edit', { defaultValue: 'Edit RPC endpoint' })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>{t('systemConfig.rpcEndpoints.fields.urlTemplate', { defaultValue: 'URL template' })}</FormLabel>
                <Input
                  value={form.urlTemplate}
                  onChange={event => setForm(prev => ({ ...prev, urlTemplate: event.target.value }))}
                />
              </FormControl>
              <HStack align="start" spacing={4}>
                <FormControl>
                  <FormLabel>{t('systemConfig.rpcEndpoints.fields.provider', { defaultValue: 'Provider' })}</FormLabel>
                  <DropdownSelect
                    value={form.provider}
                    options={PROVIDER_OPTIONS.map(provider => ({
                      value: provider,
                      label: provider,
                    }))}
                    onChange={value => setForm(prev => ({ ...prev, provider: value }))}
                    ariaLabel={t('systemConfig.rpcEndpoints.fields.provider', {
                      defaultValue: 'Provider',
                    })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.rpcEndpoints.fields.priority', { defaultValue: 'Priority' })}</FormLabel>
                  <Input
                    value={form.priority}
                    onChange={event => setForm(prev => ({ ...prev, priority: event.target.value }))}
                  />
                </FormControl>
              </HStack>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>{t('common.active')}</FormLabel>
                <Switch
                  isChecked={form.isActive}
                  onChange={event => setForm(prev => ({ ...prev, isActive: event.target.checked }))}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="admin"
                leftIcon={<FiSave />}
                onClick={handleSave}
                isLoading={updateMutation.isPending}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
